import { Cartridge, Mirroring } from '../../interfaces/cartridge';

/** Parsed ROM image handed to every mapper implementation. */
export interface RomImage {
    prgRom: Uint8Array;          // 16KB units
    chrRom: Uint8Array | null;   // 8KB units; null => CHR-RAM
    chrRamSize: number;          // bytes, used when chrRom === null
    prgRamSize: number;          // bytes (0 = none)
    mirroring: Mirroring;        // from header (may be overridden at runtime)
    battery: boolean;
    mapper: number;
    submapper: number;
    isNes2: boolean;
}

/**
 * Shared plumbing for mapper implementations: PRG/CHR storage, CHR-RAM,
 * PRG-RAM window at 0x6000-0x7FFF, and mirroring state.
 */
export abstract class BaseCartridge implements Cartridge {
    protected readonly prgRom: Uint8Array;
    protected readonly chr: Uint8Array;
    protected readonly chrIsRam: boolean;
    protected readonly prgRam: Uint8Array;
    protected mirroring: Mirroring;
    readonly battery: boolean;

    constructor(image: RomImage) {
        this.prgRom = image.prgRom;
        this.chrIsRam = image.chrRom === null;
        this.chr = image.chrRom !== null ? image.chrRom : new Uint8Array(image.chrRamSize || 0x2000);
        this.prgRam = new Uint8Array(image.prgRamSize);
        this.mirroring = image.mirroring;
        this.battery = image.battery;
    }

    abstract cpuRead(address: number): number;
    abstract cpuWrite(address: number, value: number): void;
    abstract ppuRead(address: number): number;
    abstract ppuWrite(address: number, value: number): void;

    getMirroring(): Mirroring {
        return this.mirroring;
    }

    /** PRG-RAM window 0x6000-0x7FFF. Returns true when handled. */
    protected readPrgRam(address: number, out: { value: number }): boolean {
        if (address >= 0x6000 && address < 0x8000 && this.prgRam.length > 0) {
            out.value = this.prgRam[(address - 0x6000) % this.prgRam.length];
            return true;
        }
        return false;
    }

    protected writePrgRam(address: number, value: number): boolean {
        if (address >= 0x6000 && address < 0x8000 && this.prgRam.length > 0) {
            this.prgRam[(address - 0x6000) % this.prgRam.length] = value & 0xFF;
            return true;
        }
        return false;
    }

    /** Read a 16KB PRG bank (bank index wraps). */
    protected readPrg16(bank: number, address: number): number {
        const banks = Math.max(1, this.prgRom.length / 0x4000);
        const b = ((bank % banks) + banks) % banks;
        return this.prgRom[b * 0x4000 + (address & 0x3FFF)];
    }

    /** Read an 8KB PRG bank (bank index wraps). */
    protected readPrg8(bank: number, address: number): number {
        const banks = Math.max(1, this.prgRom.length / 0x2000);
        const b = ((bank % banks) + banks) % banks;
        return this.prgRom[b * 0x2000 + (address & 0x1FFF)];
    }

    /** Read a 1KB CHR bank (bank index wraps). */
    protected readChr1(bank: number, address: number): number {
        const banks = Math.max(1, this.chr.length / 0x400);
        const b = ((bank % banks) + banks) % banks;
        return this.chr[b * 0x400 + (address & 0x3FF)];
    }

    /** Write a 1KB CHR bank (CHR-RAM only). */
    protected writeChr1(bank: number, address: number, value: number): void {
        if (!this.chrIsRam) return;
        const banks = Math.max(1, this.chr.length / 0x400);
        const b = ((bank % banks) + banks) % banks;
        this.chr[b * 0x400 + (address & 0x3FF)] = value & 0xFF;
    }
}
